import { useNotification } from '../../contexts/NotificationContext';
import { Plus, Save, Trash, Edit } from 'lucide-react';
import { adminBadgeService } from '../../services/adminBadgeService';

export interface BadgeLevelForm {
  name: string;
  threshold: number;
  image: string;
}

export interface BadgeFormData {
  id?: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  badgeImageUrl: string;
  levels: BadgeLevelForm[];
  color: string;
}

const initialBadgeForm: BadgeFormData = {
  id: undefined,
  name: '',
  slug: '',
  category: 'Academic',
  description: '',
  badgeImageUrl: '',
  levels: [
    { name: 'bronze', threshold: 100, image: 'https://i.imgur.com/rejWTfK.png' },
    { name: 'silver', threshold: 300, image: 'https://i.imgur.com/2L1DovD.png' },
    { name: 'gold', threshold: 600, image: 'https://i.imgur.com/UqiWKkQ.png' },
    { name: 'diamond', threshold: 1000, image: 'https://i.imgur.com/wPCkgUy.png' }
  ],
  color: '#4B0082'
};

// Category options
const categoryOptions = [
  'Academic',
  'Leadership',
  'Creativity',
  'Technology',
  'Environmental',
  'Social Impact',
  'Health & Wellness',
  'Critical Thinking',
  'Kindness'
];

export interface BadgeBuilderProps {
  badges: BadgeFormData[];
  setBadges: (badges: BadgeFormData[]) => void;
  formData: BadgeFormData | null;
  setFormData: (formData: BadgeFormData) => void;
  showForm: boolean;
  setShowForm: (show: boolean) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  editIndex: number | null;
  setEditIndex: (index: number | null) => void;
}

export const BadgeBuilder = ({ 
  badges, 
  setBadges, 
  formData, 
  setFormData, 
  showForm, 
  setShowForm, 
  isEditing, 
  setIsEditing, 
  editIndex, 
  setEditIndex 
}: BadgeBuilderProps) => {
  
  const { addNotification } = useNotification();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    levelIndex?: number,
    field?: keyof BadgeLevelForm
  ) => {
    if (!formData) return;

    const { name, value } = e.target;

    if (typeof levelIndex === 'number' && field) {
      const updatedLevels = formData.levels.map((lvl, idx) =>
        idx === levelIndex ? { ...lvl, [field]: field === 'threshold' ? Number(value) : value } : lvl
      );
      setFormData({ ...formData, levels: updatedLevels });
    } else {
      let updated = { ...formData, [name]: value };
      if (name === 'name' && !formData.slug) {
        updated.slug = value.toLowerCase().replace(/\s+/g, '-');
      }
      setFormData(updated);
    }
  };

  const handleLevelBlur = () => {
    if (!formData) return;
    const sorted = [...formData.levels].sort((a, b) => a.threshold - b.threshold);
    setFormData({ ...formData, levels: sorted });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData) return;

    if (!formData.name.trim() || !formData.slug.trim() || !formData.description.trim() || !formData.badgeImageUrl.trim()) {
      addNotification('Please fill in all required fields', 'error');
      return;
    }

    const sortedLevels = formData.levels.slice().sort((a, b) => a.threshold - b.threshold);
    const data = { ...formData, levels: sortedLevels };

    try {
      if (isEditing && editIndex !== null && formData.id) {
        await adminBadgeService.updateBadge(formData.id, data);
        const updated = [...badges];
        updated[editIndex] = data;
        setBadges(updated);
        addNotification('Badge updated successfully', 'success');
      } else {
        const created = await adminBadgeService.createBadge(data);
        setBadges([...badges, { ...data, id: created.badgeId }]);
        addNotification('New badge created successfully', 'success');
      }
    } catch (error) {
      console.error('Error saving badge:', error);
      addNotification('Failed to save badge', 'error');
      return;
    }
    
    // Reset form
    setFormData(initialBadgeForm);
    setIsEditing(false);
    setEditIndex(null);
    setShowForm(false);
  };

  const handleEdit = (index: number) => {
    setFormData(badges[index]);
    setIsEditing(true);
    setEditIndex(index);
    setShowForm(true);
  };

  const handleDelete = async (index: number) => {
    const badge = badges[index];
    try {
      if (badge.id) {
        await adminBadgeService.deleteBadge(badge.id);
      }
      const updatedBadges = badges.filter((_, i) => i !== index);
      setBadges(updatedBadges);
      addNotification('Badge deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting badge:', error);
      addNotification('Failed to delete badge', 'error');
    }

    if (editIndex === index) {
      setFormData(initialBadgeForm);
      setIsEditing(false);
      setEditIndex(null);
      setShowForm(false);
    }
  };

  const handleAddNew = () => {
    setFormData(initialBadgeForm);
    setIsEditing(false);
    setEditIndex(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData(initialBadgeForm);
    setIsEditing(false);
    setEditIndex(null);
    setShowForm(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Create Badge</h2>
        <button
          type="button"
          onClick={handleAddNew}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Badge
        </button>
      </div>
      
      {/* Display existing badges */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Existing Badges</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map((badge, index) => (
            <div key={index} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <h4 className="text-lg font-semibold text-gray-800">{badge.name}</h4>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(index)}
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-2">{badge.description}</p>
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-500">{badge.category}</span>
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: badge.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {showForm && formData && (
        <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEditing ? 'Edit Badge' : 'Create New Badge'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6">
              {/* Badge Name and Category in one row */}
              <div className="sm:col-span-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Badge Name *
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                  Slug *
                </label>
                <input
                  type="text"
                  name="slug"
                  id="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Badge Color and Description in one row */}
              <div className="sm:col-span-1 flex flex-col justify-end">
                <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                  Badge Color
                </label>
                <input
                  type="color"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  className="mt-1 block w-12 h-8 p-0 rounded border border-gray-300"
                />
              </div>
              <div className="sm:col-span-5">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={2}
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

              <div className="sm:col-span-6">
                <label htmlFor="badgeImageUrl" className="block text-sm font-medium text-gray-700">
                  Badge Image URL *
                </label>
                <input
                  type="url"
                  id="badgeImageUrl"
                  name="badgeImageUrl"
                  value={formData.badgeImageUrl}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                />
              </div>

            {/* Badge Levels Grid */}
            <div className="sm:col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Badge Level Images & XP Thresholds</label>
              <div className="space-y-4">
                {formData.levels.map((lvl, idx) => (
                  <div key={idx} className="grid grid-cols-5 gap-2 items-end">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600">
                        Level {idx + 1} Name
                      </label>
                      <input
                        type="text"
                        value={lvl.name}
                        onChange={e => handleInputChange(e, idx, 'name')}
                        className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-semibold text-gray-600">Image URL</label>
                      <input
                        type="url"
                        value={lvl.image}
                        onChange={e => handleInputChange(e, idx, 'image')}
                        className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600">
                        Level {idx + 1} XP
                      </label>
                      <input
                        type="number"
                        value={lvl.threshold}
                        onChange={e => handleInputChange(e, idx, 'threshold')}
                        onBlur={handleLevelBlur}
                        className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.levels
                          .filter((_, i) => i !== idx)
                          .sort((a, b) => a.threshold - b.threshold);
                        setFormData({ ...formData, levels: updated });
                      }}
                      className="text-red-600 mt-6"
                    >
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setFormData({
                      ...formData,
                      levels: [...formData.levels, { name: '', threshold: 0, image: '' }].sort(
                        (a, b) => a.threshold - b.threshold
                      )
                    })
                  }
                  className="text-indigo-600 flex items-center text-sm"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Level
                </button>
              </div>
            </div>


            <div className="pt-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Save className="h-4 w-4 mr-2" />
                {isEditing ? 'Update Badge' : 'Create Badge'}
              </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};